import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import {
  useGetAllRefundsQuery,
  useProcessRefundMutation
} from '../../../../../services/endpoints/orders/refunds';
import { User } from '../../../../../services/endpoints/people/user';
import { useGetCoursesSimpleQuery } from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { Course } from '../../../../../types/course.type';
import { Refund } from '../../../../../types/orders.type';
import { formatDate } from '../../../../../utils/dateUtils';
import {Forbidden} from "../../../../../components/error/Forbidden";
import {
  getFilterValue,
  parseRefundsDeepLinkFilters,
  preserveFilterFields
} from '../../../../../utils/listFilterSearchParams';
import { canSeeFinanceSummary } from '../../../../../utils/rbac';
import { normalizeCreatedAtFilterValue, toCreatedAtDateInputValue } from '../OrdersView';
const defaultScope = '';

const sessionDropdownQuery = {
  data: { entries: 100000, orderBy: { field: 'title', order: 'asc' } }
};

// RBAC-FE-ORDERS: Refund admin - matches backend isAdmin middleware
// Global Admin IS included for Orders domain (unlike Finance)
export const userCanAdmin_Refunds = (user: User | null) =>
    !!(user?.isSuper ||
       user?.isSupremeAdmin ||
       user?.isAdmin ||
       user?.isGlobal ||
       user?.isLocationAdmin);

export const RefundsView = () => {
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: defaultScope, data: { page: 1, entries: 10 } },
    { parseExtra: parseRefundsDeepLinkFilters }
  );
  const canAccessRefunds = userCanAdmin_Refunds(currentUser);

  const { data, error, isLoading, isFetching } = useGetAllRefundsQuery(filters, {
    skip: !canAccessRefunds
  });
  const [processRefund] = useProcessRefundMutation();
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const navigate = useNavigate();
  const {
    data: courses,
    error: coursesError,
    isLoading: coursesLoading
  } = useGetCoursesSimpleQuery(sessionDropdownQuery, { skip: !canAccessRefunds });

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      type: 'number',
      label: 'Refund ID',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'course_title',
      label: 'Session Title',
      value: getFilterValue(filters.data?.filterBy, 'course_title')
    },
    {
      name: 'course_id',
      type: 'number',
      element: (
        <FormSelect
          name="course_id"
          label="Session"
          options={
            courses?.results?.map((course: Course) => ({
              value: course.id,
              label: course.title
            })) ?? []
          }
          loadingError={coursesError ? 'Error loading sessions' : undefined}
          selected_Value={courses?.results
            ?.map((course: Course) => ({ value: course.id, label: course.title }))
            .find(
              (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'course_id')
            )}
        />
      )
    },
    {
      name: 'parent_full_name',
      label: 'Parent Name',
      value: getFilterValue(filters.data?.filterBy, 'parent_full_name')
    },
    {
      name: 'parent_email',
      label: 'Parent Email',
      value: getFilterValue(filters.data?.filterBy, 'parent_email')
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="created_at_from"
              label="Created At"
          />
      ),
      value: toCreatedAtDateInputValue(
          'greater_equal_than',
          getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
      )
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value: toCreatedAtDateInputValue(
          'lesser_equal_than',
          getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
      )
    }
  ];

  if (currentUser?.isGlobal) {
    filterFormFields.push({
      name: 'location_id',
      type: 'number',
      label: 'Location',
      element: (
          <FormSelect
              name="location_id"
              label="Location"
              options={locationOptions}
              selected_Value={locationOptions?.find(
                  (loc) => loc.value.toString() === getFilterValue(filters.data?.filterBy, 'location_id')
              )}
          />
      )
    });
  }

  function format_Date(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US');
  }

  const runRefund = async (id: number) => {
    try {
      let refund = await processRefund(id).unwrap();
      addToast('Refund processed successfully', 'success');
    } catch (error) {
      addToast('Error while processing refund', 'error');
    }
  };

  const refundColumns: ColumnDef<Refund>[] = [
    {
      header: 'ID',
      accessorKey: 'id'
    },
    {
      header: 'Order',
      accessorKey: 'order_id',
      cell: ({ row }) => (
        <LinkCell
          content={`Order #${row.original.order_id}`}
          url={`/admin/orders/orders/${row.original.order_id}`}
        />
      )
    },
    {
      header: 'Parent Name',
      accessorKey: 'parent_id',
      cell: ({ row }) => (
        <LinkCell
          content={row.original.order?.parent.full_name}
          url={`/admin/sfc-users/parents/${row.original.order?.parent.id}`}
        />
      )
    },
    {
      header: 'Session',
      accessorKey: 'course_id',
      cell: ({ row }) =>
        !row.original.order?.course ? (
          ''
        ) : (
          <LinkCell
            content={row.original.order?.course.title}
            url={`/admin/schedule/sessions/${row.original.order?.course.id}`}
          />
        )
    },
    {
      header: 'User',
      accessorKey: 'user_id',
      cell: ({ row }) => (
        <LinkCell
          content={`${row.original.user.first_name} ${row.original.user.last_name}`}
          url={`/admin/sfc-users/parents/${row.original.user_id}`}
        />
      )
    },
    {
      header: 'Total',
      accessorKey: 'total',
      cell: ({ getValue }) => <CurrencyCell value={getValue<number>()} />
    },
    {
      header: 'Transaction',
      accessorKey: 'transaction_id',
      cell: ({ row }) => {
        return !row.original?.transaction_id ? (
          <SimpleButton
            size="sm"
            height={26}
            variant={'primary'}
            onClick={() => runRefund(row.original.id)}
          >
            Process
          </SimpleButton>
        ) : (
          row.original?.transaction_id
        );
      }
    },
    {
      header: 'Refund Notes',
      accessorKey: 'notes'
    },
    {
      header: 'Created At',
      accessorKey: 'created_at',
      cell: ({ row }) => formatDate(new Date(row.original.created_at), 'MM/dd/yyyy h:mm zzz')
    },
    {
      header: () => null,
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <Stack
          direction="horizontal"
          gap={2}
        >
          <LinkCell
            content="View"
            url={`/admin/orders/refunds/${row.original.id}`}
            underline
          />
        </Stack>
      )
    }
  ];

  useEffect(() => {
    if (!isLoading && error) {
      addToast('Error while loading refunds', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  useEffect(() => {
    if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setRefunds(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    const normalizedFilterBy = filterBy.map((filter) =>
        filter.field === 'created_at'
            ? { ...filter, value: normalizeCreatedAtFilterValue(filter.operator, filter.value) }
            : filter
    );
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: preserveFilterFields(normalizedFilterBy, prev.data?.filterBy, ['order_id'])
      }
    }));
  };

  // RBAC-FE-ORDERS: Defense-in-depth guard
  if (!canAccessRefunds) {
    return <Forbidden />;
  }

  return (
    <div className="py-2">
      <SFCTable
        name={`Refunds`}
        columns={refundColumns}
        data={refunds}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters.data?.filterBy}
        indexDownloadPath="refunds"
        sidebars={
          canSeeFinanceSummary(currentUser)
              ? [
                <DetailSection title="Summary">
                  <LabelText label="Total Refunds:">
                    <CurrencyCell value={data?.totals?.total ?? 0} />
                  </LabelText>
                </DetailSection>
              ]
            : []
        }
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={(newfilters) => {
          if (typeof newfilters !== 'function') return;
          setFilters((prev) => {
            const modified = newfilters(prev);
            if (modified?.data) {
              modified.data.filterBy = preserveFilterFields(
                modified.data.filterBy || [],
                prev.data?.filterBy,
                ['order_id']
              );
            }
            return modified;
          });
        }}
      />
    </div>
  );
};
